import React from 'react';

import { Spinner } from '@/components/spinner';

import { ContentHubMediaCard } from './ContentHubMediaCard';
import styles from './ContentHubMediaGrid.module.css';

type Props = {
  medias: Api.Client.ContentHubMedia[];
  hasMore: boolean;
  isInitialLoading: boolean;
  isFetchingNextPage: boolean;
  loadMore: () => void;
  selectionMode: boolean;
  selectedMediaKeys: Set<string>;
  onToggleSelected: (mediaKey: string) => void;
};

export function ContentHubMediaGrid({
  medias,
  hasMore,
  isInitialLoading,
  isFetchingNextPage,
  loadMore,
  selectionMode,
  selectedMediaKeys,
  onToggleSelected
}: Props) {
  const sentinelRef = React.useRef<HTMLDivElement | null>(null);

  React.useEffect(() => {
    const el = sentinelRef.current;
    if (!el) return undefined;
    if (!hasMore) return undefined;

    const observer = new IntersectionObserver(
      (entries) => {
        const first = entries[0];
        if (!first?.isIntersecting) return;
        if (isFetchingNextPage) return;
        loadMore();
      },
      { root: null, rootMargin: '300px', threshold: 0 }
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, [hasMore, isFetchingNextPage, loadMore]);

  if (isInitialLoading) {
    return (
      <div className={styles.loading}>
        <Spinner />
      </div>
    );
  }

  return (
    <>
      <div className={styles.grid}>
        {medias.map((m) => (
          // media_id is not guaranteed unique across the whole list; include URL + metadata for stability
          // to avoid React key collisions (which can hide items).
          <ContentHubMediaCard
            key={`${m.media_id}:${m.contenthub_url}:${m.date_created ?? ''}:${m.filename ?? ''}`}
            media={m}
            selectionMode={selectionMode}
            isSelected={selectedMediaKeys.has(`${m.media_id}:${m.contenthub_url}`)}
            onToggleSelected={() => onToggleSelected(`${m.media_id}:${m.contenthub_url}`)}
          />
        ))}
      </div>

      <div ref={sentinelRef} />

      {isFetchingNextPage && (
        <div className={styles.loadingMore}>
          <Spinner />
        </div>
      )}
    </>
  );
}
